import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { ExternalLink, Wand2, Heart } from "lucide-react";
import AdStrip from "../components/AdStrip";
import { useAuth } from "../context/AuthContext";

const PublicOutfit = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [remixing, setRemixing] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);
  const [heartPulse, setHeartPulse] = useState(false);

  useEffect(() => {
    api.get(`/outfits/share/${slug}`).then(r => {
      setData(r.data);
      setLiked(!!r.data.liked_by_me);
      setLikeCount(Number(r.data.outfit?.like_count || 0));
    }).catch(()=>setErr(true));
    // Only re-fetch when slug changes; setters are stable, api is module-level.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto p-20 text-center font-serif-display text-3xl">This outfit is private or removed.</div>;
  if (!data) return <div className="max-w-3xl mx-auto p-20 text-center font-mono-eyebrow">Loading…</div>;

  const { outfit, items, tryon } = data;

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  const remix = async () => {
    if (!user) { nav(`/auth?mode=signup&next=/studio?remix=${slug}`); return; }
    setRemixing(true);
    try {
      // Send to studio which handles the remix POST + pre-selection
      nav(`/studio?remix=${slug}`);
    } catch (e) {
      toast.error(errText(e, "Could not remix"));
      setRemixing(false);
    }
  };

  const toggleLike = async () => {
    if (!user) { nav(`/auth?mode=signup&next=/outfits/${slug}`); return; }
    if (likeBusy) return;
    setLikeBusy(true);
    // optimistic
    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikeCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    if (nextLiked) { setHeartPulse(true); setTimeout(()=>setHeartPulse(false), 550); }
    try {
      const r = await api.post(`/outfits/${data.outfit.id}/like`);
      setLiked(r.data.liked);
      setLikeCount(Number(r.data.count || 0));
    } catch (e) {
      // rollback
      setLiked(!nextLiked);
      setLikeCount((c) => Math.max(0, c + (nextLiked ? -1 : 1)));
      toast.error(errText(e, "Could not update like"));
    } finally { setLikeBusy(false); }
  };

  const canRemix = !user || (outfit.user_id !== user.id);

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-7">
          {tryon ? (
            <div className="aspect-[3/4] overflow-hidden bg-white polaroid">
              <img src={tryon.result_data_url} alt={outfit.name} className="w-full h-full object-cover"/>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {items.map(it => <img key={it.id} src={it.image_url} alt="" className="w-full aspect-[3/4] object-cover"/>)}
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-5 md:pt-10">
          <div className="font-mono-eyebrow">Curated by {outfit.user_name}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none mt-2">{outfit.name}</h1>

          {/* Quiet heart */}
          <div className="mt-6 flex items-center gap-4">
            <button
              data-testid="like-btn"
              onClick={toggleLike}
              disabled={likeBusy}
              aria-pressed={liked}
              className="group inline-flex items-center gap-2 font-mono-eyebrow hover:text-[color:var(--accent)] transition-colors disabled:opacity-50">
              <Heart
                className={`w-5 h-5 transition-all duration-300 ${liked ? "fill-[color:var(--accent)] text-[color:var(--accent)]" : "text-black/60 group-hover:text-black"} ${heartPulse ? "scale-125" : "scale-100"}`}
                strokeWidth={liked ? 0 : 1.5}
              />
              <span data-testid="like-count">{likeCount}</span>
              <span className="text-black/40 hidden sm:inline">{likeCount === 1 ? "love" : "loves"}</span>
            </button>
            {outfit.remix_count > 0 && (
              <div className="font-mono-eyebrow text-black/50 flex items-center gap-1.5">
                <Wand2 className="w-3.5 h-3.5"/> {outfit.remix_count} remix{outfit.remix_count === 1 ? "" : "es"}
              </div>
            )}
          </div>

          {/* SHOP THE LOOK */}
          <div className="mt-10 border-t border-black/20 pt-6">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="font-mono-eyebrow text-[color:var(--accent)]">Shop the look</div>
                <div className="font-serif-display text-2xl leading-tight mt-1">{items.length} pieces, one tap each</div>
              </div>
              <a
                data-testid="shop-all-btn"
                href="#" onClick={(e)=>{e.preventDefault(); items.forEach((it, i) => setTimeout(()=>window.open(it.source_url, "_blank"), i*80));}}
                className="editorial-link font-mono-eyebrow">Open all →</a>
            </div>

            <div className="space-y-3">
              {items.map((it, i) => (
                <a key={it.id} href={it.source_url} target="_blank" rel="noreferrer"
                   data-testid={`shop-item-${it.id}`}
                   className="flex items-center gap-4 p-3 border border-black/10 hover:border-black transition-colors group bg-white">
                  <div className="w-16 h-20 overflow-hidden bg-neutral-100 shrink-0">
                    <img src={it.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-eyebrow text-black/40">{String(i+1).padStart(2,"0")} · {it.site}</div>
                    <div className="font-serif-display text-lg truncate">{it.title}</div>
                    <div className="font-mono-eyebrow text-black/60 mt-1 capitalize">{it.category}{it.price ? ` · ${it.price}` : ""}</div>
                  </div>
                  <div className="shrink-0 w-9 h-9 border border-black flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                    <ExternalLink className="w-4 h-4"/>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="flex gap-4 mt-10 pt-6 border-t border-black/10 flex-wrap">
            {canRemix && (
              <button data-testid="remix-btn" onClick={remix} disabled={remixing}
                className="btn-ink flex items-center gap-2 disabled:opacity-50">
                <Wand2 className="w-3.5 h-3.5"/> {remixing ? "Remixing…" : "Remix in my studio"}
              </button>
            )}
            <button onClick={share} data-testid="public-copy-link" className="btn-outline-ink">Copy link</button>
            <Link to="/" className="btn-outline-ink">Build your own</Link>
          </div>
        </div>
      </div>

      <AdStrip slot="outfit" heading="Shop similar" subheading="Sponsored · You might also love" limit={6} layout="grid"/>
    </div>
  );
};

export default PublicOutfit;
